import * as React from "react";
import { pdfBausteinService, type BausteinArt, type PdfBaustein } from "../../../services/pdfBausteinService";
import { bausteinAlsPng } from "./bausteinBild";
import { WerkzeugKnopf } from "./Werkzeugleiste";
import { PdfIcons } from "./pdfIcons";
import "./pdfEditor.css";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * „Unterschrift ▾“ und „Stempel ▾“ im PDF-Editor — reine Auswahl. Was danach passiert
 * (Vorschau am Mauszeiger, Klick setzt den Baustein als PDF.js-Stempel), liegt seit
 * PR 4 (02.10.2026) im Editor selbst (`PlatzierEbene`, `platzieren.ts`): so bleibt ein
 * laufendes Platzieren auch beim Reiterwechsel bestehen, denn dieses Menü wird nur im
 * Reiter „Einfügen“ gerendert.
 *
 * Die Bausteine kommen aus den Einstellungen („Unterschriften & Stempel“). Die Liste lädt
 * beim ersten Öffnen eines Menüs; kommt der Nutzer aus einem anderen Tab zurück (dort hat
 * er vielleicht gerade eine Unterschrift angelegt), lädt sie neu.
 *
 * Eigenes Menü ohne Fluent (`role="menu"` unter dem Knopf, Klick daneben oder Esc
 * schließt) — Projektregel „keine Fluent-Dialoge“, und die Leiste des Editors ist
 * ohnehin eigenes HTML.
 */

const VERWALTEN_URL = "/settings?bereich=pdf-bausteine";

/**
 * Menü-Vorschauen je Baustein und Änderungsstand (+ Tag, weil das Datum im Bild steht).
 * Das Menü baut seine Einträge bei jedem Öffnen neu auf und jedes Neuladen liefert neue
 * Objekte — ohne diesen Cache würde jedes Mal neu gemalt. Die paar Object-URLs bleiben
 * bewusst bis zum Schließen des Tabs bestehen.
 */
const vorschauCache = new Map<string, Promise<string>>();

const vorschauUrl = (b: PdfBaustein): Promise<string> => {
    const schluessel = `${b.id}|${b.geaendert ?? ""}|${new Date().toDateString()}`;
    let url = vorschauCache.get(schluessel);
    if (!url) {
        url = bausteinAlsPng(b).then((blob) => URL.createObjectURL(blob));
        url.catch(() => vorschauCache.delete(schluessel));
        vorschauCache.set(schluessel, url);
    }
    return url;
};

/** Kleine Vorschau im Menü — genau das Bild, das auch ins PDF wandert. */
const MiniVorschau: React.FC<{ b: PdfBaustein }> = ({ b }) => {
    const [url, setUrl] = React.useState<string | null>(null);
    React.useEffect(() => {
        let aktiv = true;
        vorschauUrl(b)
            .then((u) => aktiv && setUrl(u))
            .catch(() => undefined);
        return () => {
            aktiv = false;
        };
        // Nur id/Stand zählen, nicht die Objektidentität (jedes Laden liefert neue Objekte).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [b.id, b.geaendert]);
    return (
        // Maße inline: die Kachel ist genau 96 × 36 px, auch dort, wo die CSS-Datei nicht
        // geladen ist (Tests) — das Bild wird eingepasst, nie beschnitten.
        <span className="pdfx-kachel" data-baustein-vorschau="" style={{ width: 96, height: 36 }}>
            {url && <img src={url} alt="" style={{ objectFit: "contain" }} />}
        </span>
    );
};

/** Ein aufklappbares Menü unter einem Werkzeugknopf. */
const Menue: React.FC<{
    knopf: (offen: boolean, toggle: () => void) => React.ReactElement;
    onOeffnen?: () => void;
    children: (schliessen: () => void) => React.ReactNode;
}> = ({ knopf, onOeffnen, children }) => {
    const [offen, setOffen] = React.useState(false);
    const ankerRef = React.useRef<HTMLSpanElement | null>(null);
    const schliessen = React.useCallback(() => setOffen(false), []);
    const toggle = () => {
        setOffen((o) => {
            if (!o) onOeffnen?.();
            return !o;
        });
    };
    React.useEffect(() => {
        if (!offen) return;
        const draussen = (e: MouseEvent) => {
            if (!ankerRef.current?.contains(e.target as Node)) setOffen(false);
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOffen(false);
        };
        alleDokumente.addEventListener("mousedown", draussen, true);
        alleFenster.addEventListener("keydown", esc);
        return () => {
            alleDokumente.removeEventListener("mousedown", draussen, true);
            alleFenster.removeEventListener("keydown", esc);
        };
    }, [offen]);
    return (
        <span className="pdfx-menue-anker" ref={ankerRef}>
            {knopf(offen, toggle)}
            {offen && (
                <div className="pdfx pdfx-menue" role="menu">
                    {children(schliessen)}
                </div>
            )}
        </span>
    );
};

export const BausteinMenue: React.FC<{
    /** Der Nutzer hat einen Baustein gewählt — der Editor beginnt das Platzieren. */
    onWaehlen: (b: PdfBaustein) => void;
    /** Welche Art gerade platziert wird — ihr Knopf ist hervorgehoben. */
    aktiv?: BausteinArt | null;
    disabled?: boolean;
    onFehler?: (meldung: string) => void;
}> = ({ onWaehlen, aktiv, disabled, onFehler }) => {
    const [liste, setListe] = React.useState<PdfBaustein[] | null>(null);
    const [laedt, setLaedt] = React.useState(false);
    const geladen = React.useRef(false);
    // Nur wer „verwalten…“/„jetzt anlegen…“ geöffnet hat, kommt evtl. mit Neuem zurück.
    const verwaltenGeoeffnet = React.useRef(false);

    const laden = React.useCallback(async () => {
        setLaedt(true);
        try {
            setListe(await pdfBausteinService.liste());
            geladen.current = true;
        } catch (err) {
            onFehler?.(err instanceof Error ? err.message : "Unterschriften und Stempel konnten nicht geladen werden.");
            setListe([]);
        } finally {
            setLaedt(false);
        }
    }, [onFehler]);

    // Zurück aus dem Einstellungen-Tab → frisch laden, aber nur, wenn er von hier aus
    // geöffnet wurde. Jeder andere Tab-Wechsel lässt die Liste in Ruhe.
    React.useEffect(() => {
        const sichtbar = () => {
            if (document.visibilityState !== "visible" || !verwaltenGeoeffnet.current) return;
            verwaltenGeoeffnet.current = false;
            void laden();
        };
        document.addEventListener("visibilitychange", sichtbar);
        return () => document.removeEventListener("visibilitychange", sichtbar);
    }, [laden]);

    const menueOeffnen = () => {
        if (!geladen.current && !laedt) void laden();
    };

    const verwalten = () => {
        verwaltenGeoeffnet.current = true;
        window.open(VERWALTEN_URL, "_blank", "noopener");
    };

    const menue = (art: BausteinArt) => {
        const eintraege = (liste ?? []).filter((b) => b.art === art);
        const unterschrift = art === "unterschrift";
        return (
            <Menue
                onOeffnen={menueOeffnen}
                knopf={(offen, toggle) => (
                    <WerkzeugKnopf
                        menue
                        aktiv={aktiv === art}
                        icon={unterschrift ? PdfIcons.unterschrift : PdfIcons.stempel}
                        disabled={disabled}
                        label={unterschrift ? "Unterschrift" : "Stempel"}
                        title={
                            unterschrift
                                ? "Hinterlegte Unterschrift wählen und per Klick ins PDF setzen"
                                : "Stempel des Büros wählen und per Klick ins PDF setzen"
                        }
                        aria-haspopup="menu"
                        aria-expanded={offen}
                        onClick={toggle}
                    />
                )}
            >
                {(schliessen) => (
                    <>
                        {liste == null || laedt ? (
                            <button type="button" role="menuitem" disabled>
                                Wird geladen…
                            </button>
                        ) : eintraege.length === 0 ? (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    schliessen();
                                    verwalten();
                                }}
                            >
                                {unterschrift ? "Noch keine Unterschrift — jetzt anlegen…" : "Noch kein Stempel — jetzt anlegen…"}
                            </button>
                        ) : (
                            eintraege.map((b) => (
                                <button
                                    key={b.id}
                                    type="button"
                                    role="menuitem"
                                    title={b.name}
                                    onClick={() => {
                                        schliessen();
                                        onWaehlen(b);
                                    }}
                                >
                                    <MiniVorschau b={b} />
                                    <span className="pdfx-menue-name">{b.name}</span>
                                </button>
                            ))
                        )}
                        <div className="pdfx-menue-trenner" role="separator" />
                        <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                schliessen();
                                verwalten();
                            }}
                        >
                            {unterschrift ? "Unterschriften verwalten…" : "Stempel verwalten…"}
                        </button>
                    </>
                )}
            </Menue>
        );
    };

    return (
        <>
            {menue("unterschrift")}
            {menue("stempel")}
        </>
    );
};
